(() => {
  const SELECTOR = '[data-cleantra-section]';
  const RUNTIME_KEY = Symbol.for('cleantra.product-template.runtime');

  if (window[RUNTIME_KEY]?.mountAll) {
    window[RUNTIME_KEY].mountAll();
    return;
  }

  function formatMoney(cents, format, currencyCode) {
    const value = Number(cents || 0) / 100;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    if (!currencyCode) return value.toFixed(2);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(value);
  }

  function initGallery(root, signal) {
    const slides = [...root.querySelectorAll('[data-cleantra-slide]')];
    const thumbs = [...root.querySelectorAll('[data-cleantra-thumb]')];
    if (!slides.length) return;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('cleantra-is-active')));

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('cleantra-is-active', i === index);
        slide.hidden = i !== index;
      });
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i), { signal }));
    root.querySelector('[data-cleantra-prev]')?.addEventListener('click', () => show(index - 1), { signal });
    root.querySelector('[data-cleantra-next]')?.addEventListener('click', () => show(index + 1), { signal });
    const stage = root.querySelector('.cleantra-product__stage');
    let touchStartX = 0;
    let touchStartY = 0;
    stage?.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX || 0;
      touchStartY = event.changedTouches[0]?.clientY || 0;
    }, { signal, passive: true });
    stage?.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      show(deltaX < 0 ? index + 1 : index - 1);
    }, { signal, passive: true });
    root.cleantraShowMedia = (mediaId) => {
      const mediaIndex = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (mediaIndex >= 0) show(mediaIndex);
    };
    show(index);
  }

  function initProduct(root, signal) {
    const productRoot = root.querySelector('[data-cleantra-product]');
    if (!productRoot) return;
    let productData;
    try { productData = JSON.parse(productRoot.querySelector('[data-cleantra-product-json]')?.textContent || '{}'); }
    catch (error) { console.warn('Cleantra product data could not be read.', error); return; }

    const form = productRoot.querySelector('.cleantra-product__form');
    const idInput = form?.querySelector('input[name="id"]');
    const sellingPlanInput = form?.querySelector('input[name="selling_plan"]');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const oneTimePrice = productRoot.querySelector('[data-cleantra-onetime-price]');
    const oneTimeCompare = productRoot.querySelector('[data-cleantra-onetime-compare]');
    const subscriptionPrice = productRoot.querySelector('[data-cleantra-subscription-price]');
    const subscriptionCompare = productRoot.querySelector('[data-cleantra-subscription-compare]');
    const submit = productRoot.querySelector('[data-cleantra-submit]');
    const submitText = productRoot.querySelector('[data-cleantra-submit-text]');
    const planSelect = productRoot.querySelector('[data-cleantra-plan-select]');
    const planWrap = productRoot.querySelector('[data-cleantra-plan-wrap]');
    const subscriptionRadio = productRoot.querySelector('[data-cleantra-purchase-type][value="subscription"]');
    const oneTimeRadio = productRoot.querySelector('[data-cleantra-purchase-type][value="one-time"]');
    const moneyFormat = productRoot.dataset.moneyFormat || '${{amount}}';
    const currencyCode = productRoot.dataset.currencyCode || '';
    let currentVariant;

    const selectedPurchaseType = () => productRoot.querySelector('[data-cleantra-purchase-type]:checked')?.value || 'one-time';
    const currentAllocation = () => {
      if (!currentVariant || selectedPurchaseType() !== 'subscription') return null;
      const planId = planSelect?.value;
      return currentVariant.selling_plans?.find((item) => String(item.id) === String(planId)) || null;
    };

    const render = () => {
      if (!currentVariant) return;
      if (planSelect) {
        [...planSelect.options].forEach((option) => {
          option.disabled = !currentVariant.selling_plans?.some((item) => String(item.id) === option.value);
        });
        if (planSelect.selectedOptions[0]?.disabled) {
          const availableOption = [...planSelect.options].find((option) => !option.disabled);
          if (availableOption) planSelect.value = availableOption.value;
        }
      }
      if (selectedPurchaseType() === 'subscription' && !currentVariant.selling_plans?.length && oneTimeRadio) {
        oneTimeRadio.checked = true;
        if (subscriptionRadio) subscriptionRadio.checked = false;
        productRoot.querySelectorAll('[data-cleantra-purchase-card]').forEach((card) => card.classList.toggle('cleantra-is-selected', card.contains(oneTimeRadio)));
        if (planWrap) planWrap.hidden = true;
      }
      const allocation = currentAllocation() || currentVariant.selling_plans?.find((item) => String(item.id) === String(planSelect?.value)) || currentVariant.selling_plans?.[0];
      const subscriptionCurrent = allocation?.price ?? currentVariant.price;
      const subscriptionCompareValue = allocation?.compare_at_price ?? currentVariant.compare_at_price;
      if (idInput) idInput.value = currentVariant.id;
      if (sellingPlanInput) sellingPlanInput.value = selectedPurchaseType() === 'subscription' ? (allocation?.id || '') : '';
      if (oneTimePrice) oneTimePrice.textContent = formatMoney(currentVariant.price, moneyFormat, currencyCode);
      if (oneTimeCompare) {
        oneTimeCompare.textContent = currentVariant.compare_at_price > currentVariant.price ? formatMoney(currentVariant.compare_at_price, moneyFormat, currencyCode) : '';
        oneTimeCompare.classList.toggle('cleantra-is-hidden', !(currentVariant.compare_at_price > currentVariant.price));
      }
      if (subscriptionPrice) subscriptionPrice.textContent = formatMoney(subscriptionCurrent, moneyFormat, currencyCode);
      if (subscriptionCompare) {
        subscriptionCompare.textContent = subscriptionCompareValue > subscriptionCurrent ? formatMoney(subscriptionCompareValue, moneyFormat, currencyCode) : '';
        subscriptionCompare.classList.toggle('cleantra-is-hidden', !(subscriptionCompareValue > subscriptionCurrent));
      }
      if (submit) submit.disabled = !currentVariant.available;
      if (submitText) submitText.textContent = currentVariant.available ? productRoot.dataset.addText : productRoot.dataset.soldText;
      root.cleantraShowMedia?.(currentVariant.featured_media_id);
    };

    productRoot.querySelectorAll('[data-cleantra-variant]').forEach((input) => {
      input.addEventListener('change', () => {
        currentVariant = productData.variants?.find((item) => String(item.id) === input.value);
        render();
      }, { signal });
    });

    productRoot.querySelectorAll('[data-cleantra-purchase-type]').forEach((input) => {
      input.addEventListener('change', () => {
        productRoot.querySelectorAll('[data-cleantra-purchase-card]').forEach((card) => card.classList.toggle('cleantra-is-selected', card.contains(input)));
        if (planWrap) planWrap.hidden = selectedPurchaseType() !== 'subscription';
        render();
      }, { signal });
    });
    planSelect?.addEventListener('change', render, { signal });

    productRoot.querySelector('[data-cleantra-quantity-minus]')?.addEventListener('click', () => {
      if (quantityInput) quantityInput.value = Math.max(1, Number(quantityInput.value || 1) - 1);
    }, { signal });
    productRoot.querySelector('[data-cleantra-quantity-plus]')?.addEventListener('click', () => {
      if (quantityInput) quantityInput.value = Math.min(99, Number(quantityInput.value || 1) + 1);
    }, { signal });

    const selectedVariantInput = productRoot.querySelector('[data-cleantra-variant]:checked') || productRoot.querySelector('[data-cleantra-variant]');
    currentVariant = productData.variants?.find((item) => String(item.id) === selectedVariantInput?.value);
    if (selectedVariantInput) selectedVariantInput.checked = true;
    const selectedPurchase = productRoot.querySelector('[data-cleantra-purchase-type]:checked');
    productRoot.querySelectorAll('[data-cleantra-purchase-card]').forEach((card) => card.classList.toggle('cleantra-is-selected', card.contains(selectedPurchase)));
    if (planWrap) planWrap.hidden = selectedPurchaseType() !== 'subscription';
    render();
  }

  function initDeadline(root) {
    root.querySelectorAll('[data-cleantra-current-date]').forEach((node) => {
      const date = new Date();
      const day = date.getDate();
      const suffix = (day >= 11 && day <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
      node.textContent = `${date.toLocaleString('en-US', { month: 'short' })}. ${day}${suffix}`;
    });
  }

  function initFaq(root, signal) {
    const list = root.querySelector('[data-cleantra-faq-list][data-single-open="true"]');
    if (!list) return;
    list.querySelectorAll('details').forEach((item) => item.addEventListener('toggle', () => {
      if (!item.open) return;
      list.querySelectorAll('details[open]').forEach((other) => { if (other !== item) other.open = false; });
    }, { signal }));
  }

  function mount(root) {
    if (!root || root.dataset.cleantraInitialized === 'true') return;
    root.dataset.cleantraInitialized = 'true';
    const controller = new AbortController();
    root.cleantraAbortController = controller;
    initGallery(root, controller.signal);
    initProduct(root, controller.signal);
    initDeadline(root);
    initFaq(root, controller.signal);
  }

  function unmount(root) {
    root?.cleantraAbortController?.abort();
    if (root) {
      delete root.cleantraAbortController;
      delete root.cleantraShowMedia;
      delete root.dataset.cleantraInitialized;
    }
  }

  function syncProductAnchors() {
    document.querySelectorAll('[data-cleantra-product-anchor]').forEach((element, index) => {
      const uniqueId = `cleantra-product-${element.dataset.cleantraSectionId}`;
      element.id = index === 0 ? 'cleantra-product' : uniqueId;
    });
  }

  const mountAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(mount);
    syncProductAnchors();
  };
  window[RUNTIME_KEY] = { mountAll };
  mountAll();
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach(mount);
    syncProductAnchors();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach(unmount);
    setTimeout(syncProductAnchors, 0);
  });
  document.addEventListener('shopify:section:reorder', syncProductAnchors);
})();
